import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Polyline, LayersControl, LayerGroup, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.heat";
import { useNavigate } from "react-router-dom";
import { PHASES, PHASE_MAP } from "@/lib/phases";

function pinIcon(color) {
  return L.divIcon({
    className: "archive-pin",
    html: `<span style="background:${color};box-shadow:0 0 0 2px ${color},0 2px 6px rgba(0,0,0,.4)"></span>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    popupAnchor: [0, -9],
  });
}

const gratLabel = (text) =>
  L.divIcon({ className: "grat-label", html: `<span>${text}</span>`, iconSize: [46, 14], iconAnchor: [23, 7] });
const fmtLng = (v) => (v === 0 ? "0°" : `${Math.abs(v)}°${v > 0 ? "E" : "W"}`);
const fmtLat = (v) => (v === 0 ? "0°" : `${Math.abs(v)}°${v > 0 ? "N" : "S"}`);

function Graticule() {
  const lines = [], labels = [];
  for (let lng = -180; lng <= 180; lng += 30) {
    lines.push(<Polyline key={`m${lng}`} positions={[[-85, lng], [85, lng]]} pathOptions={{ color: "#9A3412", weight: 0.6, opacity: 0.25 }} />);
    labels.push(<Marker key={`ml${lng}`} position={[0.5, lng]} icon={gratLabel(fmtLng(lng))} interactive={false} keyboard={false} />);
  }
  for (let lat = -60; lat <= 60; lat += 30) {
    lines.push(<Polyline key={`p${lat}`} positions={[[lat, -180], [lat, 180]]} pathOptions={{ color: "#9A3412", weight: 0.6, opacity: 0.25 }} />);
    if (lat !== 0) labels.push(<Marker key={`pl${lat}`} position={[lat, -172]} icon={gratLabel(fmtLat(lat))} interactive={false} keyboard={false} />);
  }
  return <LayerGroup>{lines}{labels}</LayerGroup>;
}

function CoordTracker({ onMove }) {
  useMapEvents({ mousemove: (e) => onMove(e.latlng), mouseout: () => onMove(null) });
  return null;
}

function AutoResize() {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(el);
    const t = setTimeout(() => map.invalidateSize(), 200);
    return () => { ro.disconnect(); clearTimeout(t); };
  }, [map]);
  return null;
}

function ZoomWatcher({ onZoom }) {
  useMapEvents({ zoomend: (e) => onZoom(e.target.getZoom()) });
  return null;
}

const BASES = {
  "Streets": { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}", attr: "Tiles © Esri", maxNativeZoom: 18 },
  "Light Gray": { url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}", attr: "Tiles © Esri", maxNativeZoom: 16 },
  "Satellite": { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attr: "Imagery © Esri, Maxar, Earthstar Geographics", maxNativeZoom: 18 },
  "Terrain": { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Shaded_Relief/MapServer/tile/{z}/{y}/{x}", attr: "Tiles © Esri, USGS, NOAA", maxNativeZoom: 13 },
};
const LABELS_LIGHT = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}";
const LABELS_DARK = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}";
const AUTO_SAT_ZOOM = 13;

function placeholderNode(color) {
  const d = document.createElement("div");
  d.style.cssText = `height:96px;width:100%;display:flex;align-items:center;justify-content:center;background:${color}1f;background-image:repeating-linear-gradient(45deg, ${color}26 0, ${color}26 1px, transparent 1px, transparent 11px)`;
  const s = document.createElement("span");
  s.style.cssText = "font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:.12em;text-transform:uppercase;color:#78716c";
  s.textContent = "No photograph on record";
  d.appendChild(s);
  return d;
}

function popupHtml(p, color) {
  const ph = PHASE_MAP[p.phase] || PHASE_MAP.pyramids;
  const kicker = ph.roman ? `${p.country || ""} · Phase ${ph.roman}` : `${p.country || ""} · ${ph.label}`;
  const media = p.image
    ? `<img id="pimg-${p.slug}" src="${p.image}" alt="" style="height:96px;width:100%;object-fit:cover;display:block"/>`
    : `<div style="height:96px;width:100%;display:flex;align-items:center;justify-content:center;background:${color}1f;background-image:repeating-linear-gradient(45deg, ${color}26 0, ${color}26 1px, transparent 1px, transparent 11px)"><span style="font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:.12em;text-transform:uppercase;color:#78716c">No photograph on record</span></div>`;
  const noteBlock = p.note
    ? `<div style="margin-top:8px;padding:8px 10px;background:${color}14;border-left:2px solid ${color};font-family:'Plus Jakarta Sans',sans-serif;font-size:12px;line-height:1.45;color:#292524;white-space:pre-wrap">${String(p.note).replace(/</g, "&lt;")}</div>`
    : "";
  return `<div style="width:220px">
    ${media}
    <div style="padding:10px 12px">
      <div style="font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:${color}">${kicker}</div>
      <div style="font-family:'Cormorant Garamond',serif;font-size:18px;font-weight:700;line-height:1.1;color:#1c1917;margin-top:2px">${p.name}</div>
      <div style="font-family:'JetBrains Mono',monospace;font-size:10px;color:#78716c;margin-top:4px">${p.lat.toFixed(3)}, ${p.lng.toFixed(3)}</div>
      ${noteBlock}
      <a href="/pyramid/${p.slug}" id="insp-${p.slug}" style="display:inline-block;margin-top:8px;background:#1c1917;color:#fbbf24;font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:.1em;text-transform:uppercase;padding:6px 12px;text-decoration:none">Inspect Entry →</a>
    </div></div>`;
}

function ClusterGroup({ points, color, navigate }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const group = L.layerGroup();
    const icon = pinIcon(color);
    points.forEach((p) => {
      const m = L.marker([p.lat, p.lng], { icon });
      m.bindPopup(popupHtml(p, color), { closeButton: true });
      m.on("popupopen", () => {
        const a = document.getElementById(`insp-${p.slug}`);
        if (a) a.onclick = (e) => { e.preventDefault(); navigate(`/pyramid/${p.slug}`); };
        const img = document.getElementById(`pimg-${p.slug}`);
        if (img) img.onerror = () => { img.replaceWith(placeholderNode(color)); };
      });
      group.addLayer(m);
    });
    map.addLayer(group);
    return () => { map.removeLayer(group); };
  }, [points, color, map, navigate]);
  return null;
}

function FitBounds({ points, enabled }) {
  const map = useMap();
  useEffect(() => {
    if (!enabled || !points.length) return;
    if (points.length === 1) { map.setView([points[0].lat, points[0].lng], 6); return; }
    const b = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(b, { padding: [60, 60], maxZoom: 7 });
  }, [points, enabled, map]);
  return null;
}

function HeatLayer({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const latlngs = points.map((p) => [p.lat, p.lng, 0.7]);
    const layer = L.heatLayer(latlngs, {
      radius: 22,
      blur: 18,
      minOpacity: 0.28,
      maxZoom: 8,
      max: 6,
      gradient: {
        0.0: "#1D4ED8",
        0.35: "#0E7490",
        0.55: "#4D7C0F",
        0.75: "#D97706",
        1.0: "#9A3412",
      },
    });
    map.addLayer(layer);
    return () => { map.removeLayer(layer); };
  }, [points, map]);
  return null;
}

export const WorldMap = ({ points = [], height = "70vh", mode = "pins", colorBy = "phase", fitToPoints = false }) => {
  const navigate = useNavigate();
  const [coord, setCoord] = useState(null);
  const savedBase = (typeof localStorage !== "undefined" && localStorage.getItem("ae_map_base")) || "Streets";
  const initialBase = BASES[savedBase] ? savedBase : "Streets";
  const [userBase, setUserBase] = useState(initialBase);
  const [activeBase, setActiveBase] = useState(initialBase);
  const [auto, setAuto] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [showGrid, setShowGrid] = useState(true);
  const STATUS_COLORS = { wishlist: "#D97706", visited: "#4D7C0F" };

  const onZoom = (z) => {
    if (!auto) return;
    setActiveBase(z >= AUTO_SAT_ZOOM ? "Satellite" : userBase);
  };
  const pickBase = (name) => { setAuto(false); setUserBase(name); setActiveBase(name); try { localStorage.setItem("ae_map_base", name); } catch { /* ignore */ } };

  const groups = useMemo(() => {
    if (colorBy === "status") {
      const g = { wishlist: [], visited: [] };
      points.forEach((p) => (g[p.status === "visited" ? "visited" : "wishlist"].push(p)));
      return [
        { key: "wishlist", color: STATUS_COLORS.wishlist, points: g.wishlist },
        { key: "visited", color: STATUS_COLORS.visited, points: g.visited },
      ];
    }
    const m = Object.fromEntries(PHASES.map((p) => [p.key, []]));
    points.forEach((p) => (m[p.phase] || m.pyramids).push(p));
    return PHASES.map((ph) => ({ key: ph.key, color: ph.color, points: m[ph.key] }));
  }, [points, colorBy]);

  const base = BASES[activeBase];
  const labelsUrl = activeBase === "Satellite" ? LABELS_DARK : LABELS_LIGHT;

  return (
    <div className="relative border border-border shadow-sm overflow-hidden" style={{ height }} data-testid="world-map">
      <MapContainer center={[25, 15]} zoom={2} minZoom={2} maxBounds={[[-85, -180], [85, 180]]} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
        <TileLayer key={activeBase} attribution={base.attr} url={base.url} maxNativeZoom={base.maxNativeZoom} />
        {showLabels && activeBase !== "Streets" && <TileLayer key={`lbl-${activeBase === "Satellite" ? "d" : "l"}`} attribution="Labels © Esri" url={labelsUrl} maxNativeZoom={16} maxZoom={19} />}
        {showGrid && <Graticule />}
        <CoordTracker onMove={setCoord} />
        <AutoResize />
        <ZoomWatcher onZoom={onZoom} />
        <FitBounds points={points} enabled={fitToPoints} />
        {mode === "heat" ? (
          <HeatLayer points={points} />
        ) : (
          groups.map((g) => (
            <ClusterGroup key={g.key} points={g.points} color={g.color} navigate={navigate} />
          ))
        )}
      </MapContainer>

      {/* Base + overlay control */}
      <div data-testid="map-layer-control" className="absolute top-3 right-3 z-[600] bg-background/92 backdrop-blur border border-border p-2 w-40">
        <div className="flex flex-col gap-1">
          {Object.keys(BASES).map((name) => (
            <button
              key={name}
              data-testid={`base-${name.toLowerCase().replace(" ", "-")}`}
              onClick={() => pickBase(name)}
              className="text-left px-2 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors"
              style={{
                background: activeBase === name ? "hsl(var(--foreground))" : "transparent",
                color: activeBase === name ? "hsl(var(--background))" : "hsl(var(--muted-foreground))",
              }}
            >
              {name}
            </button>
          ))}
        </div>
        <div className="mt-1.5 pt-1.5 border-t border-border flex flex-col gap-1">
          <label className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-muted-foreground cursor-pointer">
            <input data-testid="toggle-labels" type="checkbox" checked={showLabels} onChange={(e) => setShowLabels(e.target.checked)} className="accent-accent" /> Labels
          </label>
          <label className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-muted-foreground cursor-pointer">
            <input data-testid="toggle-grid" type="checkbox" checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)} className="accent-accent" /> Lat / Long Grid
          </label>
          <label className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-muted-foreground cursor-pointer" title="Automatically switch to Satellite when you zoom in close">
            <input data-testid="toggle-auto-sat" type="checkbox" checked={auto} onChange={(e) => { setAuto(e.target.checked); if (!e.target.checked) setActiveBase(userBase); }} className="accent-accent" /> Auto-satellite
          </label>
        </div>
      </div>

      {mode === "heat" && (
        <div data-testid="heat-legend" className="absolute bottom-3 right-3 z-[500] bg-background/90 backdrop-blur border border-border px-3 py-2 pointer-events-none">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Mystery Density</div>
          <div className="h-2 w-40 rounded-sm" style={{ background: "linear-gradient(90deg,#1D4ED8,#0E7490,#4D7C0F,#D97706,#9A3412)" }} />
          <div className="mt-1 flex justify-between font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
            <span>Sparse</span><span>Dense</span>
          </div>
        </div>
      )}

      <div data-testid="coord-readout" className="absolute bottom-3 left-3 z-[500] bg-background/90 backdrop-blur border border-border px-3 py-2 font-mono text-[11px] tracking-wider text-foreground pointer-events-none">
        <span className="text-accent">LAT</span> {coord ? `${Math.abs(coord.lat).toFixed(2)}° ${coord.lat >= 0 ? "N" : "S"}` : "—"}
        <span className="mx-2 text-muted-foreground">·</span>
        <span className="text-accent">LNG</span> {coord ? `${Math.abs(coord.lng).toFixed(2)}° ${coord.lng >= 0 ? "E" : "W"}` : "—"}
      </div>
    </div>
  );
};
